import { useEffect, useState } from 'react';
import { formatDate, parseUserDate } from '../../model/dates';

/**
 * Free-form date entry. Shows the stored value in American format, accepts things like
 * "March 3, 1950", "3/14/1950", "about 1850", "between 1850 and 1860", and commits the
 * canonical value on blur.
 */
export function DateInput({ value, onChange, id, placeholder = 'e.g., March 3, 1950 or about 1850' }: { value?: string; onChange: (v: string | undefined) => void; id?: string; placeholder?: string }) {
  const [text, setText] = useState(() => formatDate(value, true));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(formatDate(value, true));
  }, [value, focused]);
  const parsed = text.trim() ? parseUserDate(text) : null;
  // Text left as it was shown keeps the stored value exactly: not every stored date (an imported
  // phrase, an unusual calendar) reads back character for character.
  const commit = () => onChange(text === formatDate(value, true) ? value : parseUserDate(text).value || undefined);
  const preview = parsed && parsed.understood ? formatDate(parsed.value, true) : '';
  return (
    <div className="field" style={{ gap: 2 }}>
      <input
        id={id}
        className="input"
        value={text}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter submits the surrounding form without a blur, so commit the date first.
          if (e.key === 'Enter') commit();
        }}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
      />
      {focused && parsed && (
        <div className={`hint${parsed.understood ? '' : ' warn'}`}>
          {parsed.understood ? (preview !== text.trim() ? `Will be saved as: ${preview}` : '') : "Couldn't read this as a date. It'll be saved as a note-style date."}
        </div>
      )}
    </div>
  );
}
